import { useEffect, useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "@/core/components/dv-forms/select";
import Input from "@/core/components/dv-forms/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { BinanceService } from "@/core/services/binanceService";
import { AccountType, DcaStatus } from "@/store/dca-strategy/enum";
import { useDcaStrategyStore } from "@/store/dca-strategy/store";
import Loading from "@/core/components/globals/loading";
import { get } from "lodash";
import { DcaStrategy } from "@/types/dca";
import { TransferFundsType } from "@/types/binance";
import { Account } from "@/types/user";
import { useTranslation } from "react-i18next";
import { STALE_TIME } from "@/core/constants/tanstack";
import { FEATURE_FLAGS } from "@/core/constants/feature-flags";

const TransferForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const {
    setDcaStrategyDetail,
    setDcaStrategyCreate,
    toDcaWallet,
    setToDcaWallet
  } = useDcaStrategyStore();
  const { register, handleSubmit, control, formState: { errors, isValid }, watch, setValue, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      from: '',
      to: '',
      amount: 0,
    }
  });
  const [listAccount, setListAccount] = useState<Account[]>([]);
  const queryClient = useQueryClient();

  // Filter out Exchange accounts if Exchange is disabled
  const filteredListAccount = useMemo(() => {
    if (!FEATURE_FLAGS.EXCHANGE_ENABLED) {
      return listAccount.filter(account => account.type !== AccountType.EXCHANGE);
    }
    return listAccount;
  }, [listAccount]);

  const { data: accountsData, isLoading: isLoadingGetAllAccount } = useQuery({
    queryKey: ['allAccounts'],
    queryFn: () => BinanceService.getAllAccount(),
    staleTime: STALE_TIME.CRITICAL, // 15 seconds - accounts change frequently
  });

  useEffect(() => {
    if (accountsData) {
      const list = accountsData || [];
      setListAccount(list);
      setValue('to', list.filter((account) => account.type === AccountType.DCA)
        ?.find((account) => account.id === get(toDcaWallet, 'id', ''))?.subAccountId || '');
      const master = list.find((d) => d.type === AccountType.MASTER);
      if (master && master?.usdcBalance > 0) {
        setValue('from', master.subAccountId);
      }
    }
  }, [accountsData, toDcaWallet, setValue]);

  const { mutate: createTransferFunds, isLoading } = useMutation({
    mutationFn: (data: TransferFundsType) => BinanceService.createTransferFunds(data),
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['allAccounts'] });
      
      // Snapshot previous value
      const previousAccounts = queryClient.getQueryData(['allAccounts']);
      
      // Optimistically update account balances
      queryClient.setQueryData(['allAccounts'], (old: Account[] | undefined) => {
        if (!old) return old;
        return old.map((account: Account) => {
          if (account.subAccountId === variables.from.accountId) {
            return { ...account, usdcBalance: account.usdcBalance - variables.amount };
          }
          if (account.subAccountId === variables.to.accountId) {
            return { ...account, usdcBalance: account.usdcBalance + variables.amount };
          }
          return account;
        });
      });
      
      return { previousAccounts };
    },
    onError: (err, variables, context: any) => {
      // Rollback on error
      if (context?.previousAccounts) {
        queryClient.setQueryData(['allAccounts'], context.previousAccounts);
      }
    },
    onSuccess: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['allAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['mainAccountOverview'] });
      queryClient.invalidateQueries({ queryKey: ['mainAccountAllocations'] });
      queryClient.invalidateQueries({ queryKey: ['exchangeOverview'] });
      queryClient.invalidateQueries({ queryKey: ['exchangeAllocations'] });
      queryClient.invalidateQueries({ queryKey: ['dcaStrategies'] });
      queryClient.invalidateQueries({ queryKey: ['walletOverview'] });
      
      const fromAccount = listAccount.find((account) => account.subAccountId === getValues('from'));
      const toAccount = listAccount.find((account) => account.subAccountId === getValues('to'));
      toast.success(t("message.transfer_success", { amount: getValues('amount'), from: fromAccount?.name, to: toAccount?.name }));
      
      // Use getState() to get fresh store values and avoid stale closure issues
      const currentState = useDcaStrategyStore.getState();
      
      if (toDcaWallet?.dcaStatus === DcaStatus.EDITING) {
        setDcaStrategyCreate({
          ...currentState.dcaStrategyCreate,
          investedCapital: getValues('amount') + get(currentState.dcaStrategyCreate, 'investedCapital', 0)
        } as DcaStrategy)
      } else {
        setDcaStrategyDetail({
          ...currentState.dcaStrategyDetail,
          usdcBalance: getValues('amount') + get(currentState.dcaStrategyDetail, 'usdcBalance', 0)
        } as DcaStrategy)
      }
      onClose();
    },
  });

  const from = watch('from');
  const to = watch('to');

  const onSubmit = (values: any) => {
    const fromAccount = listAccount.find((account) => account.subAccountId === values.from);
    const toAccount = listAccount.find((account) => account.subAccountId === values.to);
    if (fromAccount && toAccount) {
      createTransferFunds({
        amount: Number(values.amount),
        from: {
          accountId: values.from,
          accountType: fromAccount.type,
          email: fromAccount.subAccountEmail,
        },
        to: {
          accountId: values.to,
          accountType: toAccount.type,
          email: toAccount.subAccountEmail,
        },
      });
    }
  }

  const getDisabled = (account: Account, accountTargetId: string) => {
    if (accountTargetId) {
      const accountTarget = filteredListAccount.find((l) => l.subAccountId === accountTargetId);

      // Check if both accounts are valid
      if (account && accountTarget) {
        // Master can transfer to DCA, Mandate, and Exchange
        if (account.type === AccountType.MASTER) {
          return ![AccountType.DCA, AccountType.MANDATE, AccountType.EXCHANGE].includes(accountTarget.type);
        }

        // DCA, Mandate, and Exchange can only transfer to Master
        if ([AccountType.DCA, AccountType.MANDATE, AccountType.EXCHANGE].includes(account.type)) {
          return accountTarget.type !== AccountType.MASTER;
        }
      }
    }

    // Default case: Enable the option if no target account is selected
    return false;
  };

  const validateAmount = () => {
    if (filteredListAccount.length === 0) {
      return undefined;
    }
    const isMasterWallet = from === filteredListAccount.find((l) =>l.type === AccountType.MASTER)?.subAccountId;
    return isMasterWallet ? { value: 10, message: t("form.invalid.min_transfer") } : undefined;
  }

  const validateSelectWallet = (value: string) => {
    if (filteredListAccount.find((account) => account.subAccountId === value)?.usdcBalance === 0) {
      return t("form.invalid.no_balance_transfer");
    }
    return true;
  }

  useEffect(() => {
    return () => {
      setToDcaWallet(null);
    };
  }, []);

  return (
    <>
      <div className="general-modal__content" style={{ overflow: 'unset' }}>
        <div>
          {
            isLoadingGetAllAccount ? (
              <Loading />
            ) : (
              <form className="dv-form">
                <Controller
                  name="from"
                  control={control}
                  rules={{ required: t("form.required.deposit"), validate: (value) => validateSelectWallet(value) }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      closeEnable
                      label={t("common.transfer_from_wallet")}
                      placeholder=""
                      options={filteredListAccount.map((account) => ({
                        content: `${account.name} (${account.usdcBalance} USDC)`,
                        value: account.subAccountId,
                        disabled: getDisabled(account, to),
                      }))}
                      errors={errors}
                    />
                  )}
                />
                <Controller
                  name="to"
                  control={control}
                  rules={{ required: t("form.required.deposit") }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      closeEnable
                      label={t("common.transfer_to_wallet")}
                      placeholder=""
                      options={filteredListAccount.map((account) => ({
                        content: `${account.name} (${account.usdcBalance} USDC)`,
                        value: account.subAccountId,
                        disabled: getDisabled(account, from),
                      }))}
                      disabled={!!toDcaWallet}
                    />
                  )}
                />
                <Input
                  name="amount"
                  label={t("common.amount_to_transfer")}
                  placeholder=""
                  register={register}
                  errors={errors}
                  startAdornment={`$`}
                  type="number"
                  rules={{ required: t("form.required.amount"), valueAsNumber: true, min: validateAmount() }}
                  endAdornment={
                    <button
                      type="button"
                      className="max-button"
                      disabled={!from}
                      onClick={() => {
                        const fromAccount = filteredListAccount.find((account) => account.subAccountId === from);
                        if (fromAccount) {
                          setValue('amount', fromAccount.usdcBalance);
                        }
                      }}
                    >
                      {t("common.max")}
                    </button>
                  }
                />
              </form>
            )
          }
        </div>
      </div>
      <div className="general-modal__footer">
        <button disabled={!isValid || isLoading} className="cta-button cta-large cta-blue" onClick={handleSubmit(onSubmit)}>
          <span>{t(isLoading ? 'common.transferring...' : 'common.transfer')}</span>
        </button>
      </div>
    </>
  )
}

export default TransferForm;
